import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/apiClient';
import './FormCustomizationPanel.css';

const FormCustomizationPanel = ({ jobId }) => {
    const [formSettings, setFormSettings] = useState({
        form_background_color: '#FFFFFF',
        form_text_color: '#000000',
        form_primary_color: '#667eea',
        form_show_job_title: true,
        form_show_job_description: true,
        form_show_linkedin_field: true,
        form_require_google_auth: false,
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchFormSettings();
    }, [jobId]);

    const fetchFormSettings = async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/form-settings`, {
            });
            if (response.ok) {
                const data = await response.json();
                setFormSettings({
                    ...formSettings,
                    ...data,
                    form_primary_color: data.form_primary_color || '#667eea',
                    form_require_google_auth:
                        data.form_require_google_auth ?? false,
                });
            }
        } catch (error) {
            console.error('Error fetching form settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/form-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formSettings)
            });
            
            if (response.ok) {
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);
            } else {
                alert('Failed to save form settings');
            }
        } catch (error) {
            console.error('Error saving form settings:', error);
            alert('Error saving form settings');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setFormSettings({
            ...formSettings,
            [field]: value
        });
    };

    // Color options for dropdowns
    const backgroundColorOptions = [
        { value: '#f5f5f5', label: 'Light Gray (Default)' },
        { value: '#FFFFFF', label: 'White' },
        { value: '#e3f2fd', label: 'Light Blue' },
        { value: '#f3e5f5', label: 'Light Purple' },
        { value: '#fff3e0', label: 'Light Orange' },
        { value: '#e8f5e9', label: 'Light Green' },
        { value: '#fce4ec', label: 'Light Pink' },
        { value: '#f1f8e9', label: 'Light Yellow-Green' }
    ];

    const textColorOptions = [
        { value: '#333333', label: 'Dark Gray (Default)' },
        { value: '#000000', label: 'Black' },
        { value: '#424242', label: 'Dark Gray' },
        { value: '#616161', label: 'Medium Gray' },
        { value: '#1976d2', label: 'Blue' },
        { value: '#388e3c', label: 'Green' },
        { value: '#d32f2f', label: 'Red' },
        { value: '#f57c00', label: 'Orange' }
    ];

    const primaryColorOptions = [
        { value: '#667eea', label: 'Purple (Default)' },
        { value: '#1976d2', label: 'Blue' },
        { value: '#388e3c', label: 'Green' },
        { value: '#d32f2f', label: 'Red' },
        { value: '#f57c00', label: 'Orange' },
        { value: '#7b1fa2', label: 'Deep Purple' },
        { value: '#0288d1', label: 'Light Blue' },
        { value: '#00796b', label: 'Teal' }
    ];

    if (loading) {
        return <div className="form-customization-panel">Loading...</div>;
    }

    return (
        <div className="form-customization-panel">
            <h3>Form Customization</h3>
            <p className="help-text">
                Customize the appearance and content of the application form.
            </p>

            <div className="color-settings">
                <div className="color-group">
                    <label>Background Color</label>
                    <select
                        value={formSettings.form_background_color}
                        onChange={(e) => handleChange('form_background_color', e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '1rem' }}
                    >
                        {backgroundColorOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>

                <div className="color-group">
                    <label>Text Color</label>
                    <select
                        value={formSettings.form_text_color}
                        onChange={(e) => handleChange('form_text_color', e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '1rem' }}
                    >
                        {textColorOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>

                <div className="color-group">
                    <label>Primary Color (Buttons)</label>
                    <select
                        value={formSettings.form_primary_color}
                        onChange={(e) => handleChange('form_primary_color', e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '1rem' }}
                    >
                        {primaryColorOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="visibility-settings">
                <h4>Display Options</h4>
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={formSettings.form_show_job_title}
                        onChange={(e) => handleChange('form_show_job_title', e.target.checked)}
                    />
                    Show Job Title on Form
                </label>
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={formSettings.form_show_job_description}
                        onChange={(e) => handleChange('form_show_job_description', e.target.checked)}
                    />
                    Show Job Description on Form
                </label>
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={formSettings.form_show_linkedin_field}
                        onChange={(e) => handleChange('form_show_linkedin_field', e.target.checked)}
                    />
                    Show LinkedIn Profile URL Field
                </label>
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={formSettings.form_require_google_auth}
                        onChange={(e) => handleChange('form_require_google_auth', e.target.checked)}
                    />
                    Require Google sign-in to apply (block duplicate applications)
                </label>
            </div>

            <div className="save-actions">
                <button 
                    onClick={handleSave} 
                    disabled={saving}
                    className="save-button"
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </button>
                {saved && <span className="saved-indicator">✓ Saved!</span>}
            </div>
        </div>
    );
};

export default FormCustomizationPanel;
